import { TitleCasePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PAMPRestAPIService } from '../../../core/api/pamp-rest-api.service';
import { PatientService } from '../../../core/api/patient.service';
import {
  ComplianceGrade,
  ComplianceLoadState,
  MedicationIssue,
  PatientCompliance,
  PeriodCompliance
} from 'src/app/core/models/patient-compliance.model';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { PampIframeService } from 'src/app/core/services/pamp-iframe.service';
import {
  buildPatientAppHistory,
  buildPatientAppState,
  PatientAppCycleEvent,
  PatientAppEnrolmentCycle,
  PatientAppState
} from 'src/app/patients/patient-app-timeline.util';
import { LocaleDatePipe } from 'src/app/shared/shared-pipes/locale-date.pipe';
import { safeParseJson } from 'src/app/shared/util/helper.util';
import { PatientTodoHistoryComponent } from 'src/app/patients/patient-todo-history/patient-todo-history.component';
import { RecordDetailHost } from '../record-detail-panel/record-detail-host';

interface PatientAppCycleView extends PatientAppEnrolmentCycle {
  isOpen: boolean;
}
import { SideSheetService } from '../../../shared/side-sheet/side-sheet.service';
import { RecordDetail, RecordDetailSection } from '../record-detail-panel/record-detail.model';

export type PatientAppTab = 'progress' | 'tests' | 'activity';
export type ComplianceActivity = 'medication' | 'exercise';

export interface ComplianceActivityRow {
  activity: ComplianceActivity;
  label: string;
  lastLogDate: string | null;
  hasNeverLogged: boolean;
  periodCompliance: PeriodCompliance[];
}

@Component({
  selector: 'csi-compliance-table',
  templateUrl: './compliance-table.component.html',
  styleUrls: ['./compliance-table.component.scss'],
  providers: [TitleCasePipe, LocaleDatePipe]
})
export class ComplianceTableComponent extends RecordDetailHost<ComplianceActivity>
  implements OnInit, OnDestroy {
  public complianceData: PatientCompliance | null = null;
  public isLoading = true;

  private readonly patientIdToFetch = new Subject<string>();

  public lastIssueDate: string | null = null;
  public activityRows: ComplianceActivityRow[] = [];
  public complianceWindows: PeriodCompliance[] = [];
  public medicationIssue: MedicationIssue | null = null;
  public loadState: ComplianceLoadState = 'notEnrolled';
  public hasNeverLoggedAnything = false;

  public readonly patientAppTabs: PatientAppTab[] = ['progress', 'tests', 'activity'];
  public readonly tabLabelMap: { [tab in PatientAppTab]: string } = {
    progress: 'Progress',
    tests: 'Self test(s)',
    activity: 'Activity'
  };

  public activeTab: PatientAppTab = 'progress';
  public appStatusLabel = '';
  public appStatusBadgeClass = 'badge-gray';
  public invitedDate: string | null = null;
  public registeredDate: string | null = null;
  public currentStatusDate: string | null = null;
  public patientAppState: PatientAppState = null;
  // Only present while the self test tab is open — it is behind an *ngIf.
  @ViewChild(PatientTodoHistoryComponent, { static: false })
  private selfTestHistory: PatientTodoHistoryComponent;

  public patientAppCycles: PatientAppCycleView[] = [];
  public patientAppEnrolmentCount = 0;

  private readonly compliancePeriodsInDays = [7, 30, 90, 180];

  constructor(
    private patientService: PatientService,
    private pampRestAPIService: PAMPRestAPIService,
    private pampIframeService: PampIframeService,
    private appStateService: AppStateService,
    private route: ActivatedRoute,
    private localeDatePipe: LocaleDatePipe,
    sideSheetService: SideSheetService
  ) {
    super(sideSheetService);
  }

  ngOnInit() {
    this.refreshPatientAppStatus();
    this.listenForComplianceFetches();

    this.appStateService.currentPatient$.pipe(untilDestroyed(this)).subscribe(() => {
      this.refreshPatientAppStatus();
    });

    if (this.route.parent) {
      this.route.parent.params.pipe(untilDestroyed(this)).subscribe(params => {
        const patientId = params['id'];
        if (patientId) {
          this.complianceData = null;
          this.refreshComplianceProperties();
          this.fetchCompliance(patientId);
        } else {
          this.refreshComplianceProperties();
          this.isLoading = false;
        }
      });
    } else {
      this.refreshComplianceProperties();
      this.isLoading = false;
    }

    this.pampIframeService.iframeClosed$.pipe(untilDestroyed(this)).subscribe(() => {
      this.refreshData();
    });
  }

  ngOnDestroy() {
    this.closeDetail();
  }

  private refreshComplianceProperties() {
    const medicationCompliance = this.getPeriodComplianceDataByCategory('meds');
    const exerciseCompliance = this.getPeriodComplianceDataByCategory('exercise');
    const lastMedicationDate = this.complianceData
      ? this.complianceData.lastMedicationReportedDate
      : null;
    const lastExerciseDate = this.complianceData
      ? this.complianceData.lastExerciseReportedDate
      : null;

    this.lastIssueDate = this.complianceData ? this.complianceData.issueReportedDate : null;
    this.medicationIssue = this.getMedicationIssue();
    this.complianceWindows = medicationCompliance;

    this.activityRows = [
      {
        activity: 'medication',
        label: 'Medication',
        lastLogDate: lastMedicationDate,
        hasNeverLogged: !!this.complianceData && !lastMedicationDate,
        periodCompliance: medicationCompliance
      },
      {
        activity: 'exercise',
        label: 'Exercise',
        lastLogDate: lastExerciseDate,
        hasNeverLogged: !!this.complianceData && !lastExerciseDate,
        periodCompliance: exerciseCompliance
      }
    ];

    this.hasNeverLoggedAnything = this.activityRows.every(activityRow => activityRow.hasNeverLogged);
  }

  public selectTab(tab: PatientAppTab) {
    this.activeTab = tab;
  }

  public selectActivity(activityRow: ComplianceActivityRow) {
    this.toggleDetail(activityRow.activity, () => this.buildComplianceDetail(activityRow));
  }

  public toggleCycle(cycle: PatientAppCycleView): void {
    cycle.isOpen = !cycle.isOpen;
  }

  public trackByCycle(index: number, cycle: PatientAppCycleView): number {
    return cycle.cycleNumber;
  }

  public trackByCycleEvent(index: number, cycleEvent: PatientAppCycleEvent): string {
    return cycleEvent.eventLabel + '|' + (cycleEvent.date || index);
  }

  public trackByActivity(index: number, activityRow: ComplianceActivityRow): string {
    return activityRow.activity;
  }

  public trackByPeriod(index: number, periodCompliance: PeriodCompliance): number {
    return periodCompliance.period;
  }

  public trackByTab(index: number, tab: PatientAppTab): string {
    return tab;
  }

  public trackByMedicationName(index: number, medication: { productName?: string }): string {
    return medication.productName || String(index);
  }

  private buildComplianceDetail(activityRow: ComplianceActivityRow): RecordDetail {
    const sections: RecordDetailSection[] = [
      {
        title: 'Windows',
        rows: activityRow.periodCompliance.map(periodCompliance => ({
          label: 'Last ' + periodCompliance.period + ' days',
          value: periodCompliance.display,
          isValueMuted: periodCompliance.status === 'unknown',
          badgeLabel: periodCompliance.logs > 0 ? periodCompliance.logs + ' logged' : null
        }))
      }
    ];

    if (activityRow.hasNeverLogged) {
      sections.push({
        title: 'What this means',
        note: 'There is no denominator to measure against, so adherence here is unknown — not zero.',
        rows: []
      });
    }

    return {
      recordType: 'Patient app · Progress',
      title: activityRow.label + ' log',
      subtitle: activityRow.hasNeverLogged
        ? 'No log has ever been recorded'
        : 'Last log ' + (this.localeDatePipe.transform(activityRow.lastLogDate, 'MMM d, y') || '—'),
      summaryValue: activityRow.periodCompliance.length
        ? activityRow.periodCompliance[0].display
        : '—',
      isSummaryMuted: activityRow.hasNeverLogged,
      summaryBadgeLabel: activityRow.hasNeverLogged ? 'Never logged' : null,
      sections: sections
    };
  }

  private refreshPatientAppStatus() {
    const patient = this.patientService && this.patientService.patient;
    this.patientAppState = buildPatientAppState(patient);

    this.appStatusLabel = this.patientAppState.statusLabel;
    this.appStatusBadgeClass = this.patientAppState.badgeClass;
    this.invitedDate = this.patientAppState.invitedDate;
    this.registeredDate = this.patientAppState.registeredDate;
    this.currentStatusDate = this.patientAppState.currentStatusDate;

    const patientAppHistory = buildPatientAppHistory(patient);
    this.patientAppEnrolmentCount = patientAppHistory.enrolmentCount;
    // Rebuilt on every patient emission, so an already-expanded cycle keeps its state instead
    // of snapping shut when an unrelated mutation fires.
    const wasOpenByCycleNumber: { [cycleNumber: number]: boolean } = {};
    this.patientAppCycles.forEach(existingCycle => {
      wasOpenByCycleNumber[existingCycle.cycleNumber] = existingCycle.isOpen;
    });

    this.patientAppCycles = patientAppHistory.cycles.map(cycle => ({
      cycleNumber: cycle.cycleNumber,
      isCurrent: cycle.isCurrent,
      startDate: cycle.startDate,
      endDate: cycle.endDate,
      events: cycle.events,
      isOpen: wasOpenByCycleNumber.hasOwnProperty(String(cycle.cycleNumber))
        ? wasOpenByCycleNumber[cycle.cycleNumber]
        : cycle.isCurrent
    }));
  }

  public openPatientApp() {
    if (!this.patientService.patient) {
      return;
    }

    this.pampIframeService.openPatientDashboard(this.patientService.patient.id);
  }

  public refreshData() {
    if (this.patientService.patient && this.patientService.patient.id) {
      this.fetchCompliance(this.patientService.patient.id);
    }
    // The self test list is its own REST call in a child component, so the column's refresh has
    // to reach it too — otherwise a completed test only appears after the tab is left and reopened.
    if (this.selfTestHistory) {
      this.selfTestHistory.refreshData();
    }
  }

  private fetchCompliance(patientId: string) {
    this.isLoading = true;
    this.patientIdToFetch.next(patientId);
  }

  private listenForComplianceFetches() {
    this.patientIdToFetch
      .pipe(
        switchMap(patientId =>
          this.pampRestAPIService.getPatientCompliance(patientId).pipe(
            map(complianceResponse => ({ complianceResponse: complianceResponse, hasFailed: false })),
            catchError(error => {
              console.error(
                'Error fetching compliance data from API:',
                error && error.error && error.error.message ? error.error.message : error
              );
              return of({ complianceResponse: null, hasFailed: true });
            })
          )
        ),
        untilDestroyed(this)
      )
      .subscribe(fetchResult => {
        const complianceResponse = fetchResult.complianceResponse;
        if (fetchResult.hasFailed) {
          this.complianceData = null;
          this.loadState = 'failed';
        } else if (!complianceResponse || !complianceResponse.patientId) {
          this.complianceData = null;
          this.loadState = 'notEnrolled';
        } else {
          this.complianceData = complianceResponse;
          this.loadState = 'loaded';
        }
        this.refreshComplianceProperties();
        this.isLoading = false;
      });
  }

  private getPeriodComplianceDataByCategory(category: 'meds' | 'exercise'): PeriodCompliance[] {
    if (!this.complianceData) {
      return [];
    }
    const complianceRecord = this.complianceData;
    const fieldPrefix = category === 'meds' ? 'medicationCompliance' : 'exerciseCompliance';

    return this.compliancePeriodsInDays.map(daysPeriod => {
      const compliancePercentageField = fieldPrefix + daysPeriod + 'Days';
      const complianceInfoField = compliancePercentageField + 'Info';

      const rawPercentageValue = complianceRecord[compliancePercentageField];
      const hasReportedPercentage =
        rawPercentageValue !== undefined &&
        rawPercentageValue !== null &&
        !isNaN(rawPercentageValue);
      const compliancePercentage = hasReportedPercentage ? Number(rawPercentageValue) : null;

      const complianceInfo = safeParseJson(complianceRecord[complianceInfoField]);
      const logCount =
        complianceInfo && complianceInfo.totalDayEntry !== undefined
          ? complianceInfo.totalDayEntry
          : 0;
      const startDate = complianceInfo && complianceInfo.startDate ? complianceInfo.startDate : null;
      const endDate = complianceInfo && complianceInfo.endDate ? complianceInfo.endDate : null;

      return {
        period: daysPeriod,
        percentage: compliancePercentage,
        display: compliancePercentage === null ? '—' : compliancePercentage + '%',
        logs: logCount,
        startDate: startDate,
        endDate: endDate,
        status: this.getComplianceGrade(compliancePercentage)
      };
    });
  }

  private getComplianceGrade(compliancePercentage: number | null): ComplianceGrade {
    if (compliancePercentage === null) {
      return 'unknown';
    }
    if (compliancePercentage >= 80) {
      return 'green';
    }
    if (compliancePercentage >= 50) {
      return 'orange';
    }
    return 'red';
  }

  private getMedicationIssue(): MedicationIssue | null {
    if (!this.complianceData) {
      return null;
    }
    const complianceRecord = this.complianceData;
    const issueMedications = safeParseJson(complianceRecord.issueMeds) || [];

    return {
      hasIssue: complianceRecord.hasIssue,
      meds: issueMedications,
      note: complianceRecord.lastIssueNote || null,
      reportedDate: complianceRecord.issueReportedDate ? complianceRecord.issueReportedDate : null
    };
  }
}
